import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Tag,
  message,
  Popconfirm,
  Typography,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ApiOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';
import Flex from 'components/shared-components/Flex';
import {
  getAiModels,
  createAiModel,
  updateAiModel,
  deleteAiModel,
  testAiModel,
} from 'services/aiService';

const { Text } = Typography;

const PROVIDER_OPTIONS = [
  { value: 'nvidia', label: 'NVIDIA NIM (Free)' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'gemini', label: 'Google Gemini' },
];

const ManageApiKeys = () => {
  const [form] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editRecord, setEditRecord] = useState(null);

  const loadModels = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAiModels();
      const list = Array.isArray(res?.data) ? res.data : [];
      setRows(list);
    } catch (err) {
      message.error(err?.response?.data?.message || 'Failed to load API keys');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadModels();
  }, [loadModels]);

  const openCreate = () => {
    setEditId(null);
    setEditRecord(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditId(record.id);
    setEditRecord({
      provider: record.provider,
      model: record.model,
      label: record.label || '',
      is_active: record.is_active !== false,
      // api_key intentionally omitted — not returned by API
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload = {
        provider: values.provider,
        model: values.model?.trim(),
        label: values.label?.trim() || null,
        is_active: values.is_active !== false,
      };

      if (values.api_key?.trim()) {
        payload.api_key = values.api_key.trim();
      } else if (!editId) {
        message.error('API key is required for new entries');
        setSaving(false);
        return;
      }

      if (editId) {
        await updateAiModel(editId, payload);
        message.success('API key updated');
      } else {
        await createAiModel(payload);
        message.success('API key added');
      }

      setModalOpen(false);
      loadModels();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteAiModel(id);
      message.success('API key deleted');
      loadModels();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Delete failed');
    }
  };

  const handleTest = async (id) => {
    setTestingId(id);
    try {
      const res = await testAiModel(id);
      if (res?.status) {
        message.success(res.message || 'Connection successful');
      } else {
        message.error(res?.message || 'Connection test failed');
      }
    } catch (err) {
      message.error(err?.response?.data?.message || 'Connection test failed');
    } finally {
      setTestingId(null);
    }
  };

  const columns = [
    {
      title: 'Label',
      dataIndex: 'display_label',
      render: (_, record) => record.display_label || record.label || '—',
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      render: (provider) => (
        <Tag color="purple">{(provider || '').toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Model',
      dataIndex: 'model',
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      render: (active) =>
        active ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>,
    },
    {
      title: 'Key',
      dataIndex: 'has_api_key',
      render: (has) => (has ? 'Configured' : 'Missing'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Flex alignItems="center" style={{ gap: 12 }}>
          <Button
            type="link"
            size="small"
            icon={<ExperimentOutlined />}
            loading={testingId === record.id}
            onClick={() => handleTest(record.id)}
          >
            Test
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEdit(record)}
          />
          <Popconfirm
            title="Delete this API key?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Flex>
      ),
    },
  ];

  return (
    <>
      <Card
        title={
          <Flex alignItems="center" style={{ gap: 8 }}>
            <ApiOutlined />
            <span>Manage API Keys</span>
          </Flex>
        }
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add API Key
          </Button>
        }
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          AI models for chatbot flows are assigned automatically on the server
          (one model per session). Add OpenAI, Anthropic, or Gemini keys here.
        </Text>
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editId ? 'Edit API Key' : 'Add API Key'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        confirmLoading={saving}
        width={520}
      >
        <Form
          form={form}
          layout="vertical"
          key={editId ?? 'create'}
          initialValues={
            editRecord ?? { is_active: true, provider: 'nvidia' }
          }
        >
          <Form.Item
            name="provider"
            label="Provider"
            rules={[{ required: true, message: 'Select a provider' }]}
          >
            <Select options={PROVIDER_OPTIONS} placeholder="Select provider" />
          </Form.Item>
          <Form.Item
            name="model"
            label="Model name"
            rules={[{ required: true, message: 'Enter model name' }]}
            extra="NVIDIA: copy from build.nvidia.com (e.g. meta/llama-3.1-8b-instruct, mistralai/mistral-7b-instruct-v0.2)"
          >
            <Input placeholder="Model identifier" />
          </Form.Item>
          <Form.Item name="label" label="Label (optional)">
            <Input placeholder="Display name in admin" />
          </Form.Item>
          <Form.Item
            name="api_key"
            label={editId ? 'API key (leave blank to keep current)' : 'API key'}
            rules={editId ? [] : [{ required: true, message: 'Enter API key' }]}
          >
            <Input.Password placeholder="NVIDIA API key from build.nvidia.com" autoComplete="new-password" />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ManageApiKeys;
